import { useCallback, type KeyboardEvent, type ClipboardEvent, type MouseEvent as ReactMouseEvent } from 'react'
import { toast } from 'sonner'
import type { DocumentReference, ChatImage } from '@/components/layout/chat-pane.types'
import type { SlashSkillRow } from './slash-command-menu'
import {
  CHAT_IMAGE_BUDGET_TOAST_TITLE,
  validateStagedImages,
} from '@/lib/chat-image-budget'
import { resizeChatImageFile } from '@/lib/chat-image-resize'
import { isLongTextPaste } from '@/lib/local-text-import'
import { partitionComposerDropFiles } from '@/lib/chat-audio-import'
import {
  extractPlainText,
  renderChipAsHTML,
  renderSkillChipAsHTML,
  renderUrlChipAsHTML,
  renderCodeBlockAsHTML,
  documentReferenceChipPresentInEditor,
  referenceListedWithSameKey,
  parseMentionQueryAtCursor,
  clipPasteReferenceExcerpt,
  clipPasteReferenceTail,
} from './contenteditable-chat-input.utils'
import {
  armReferenceChipTooltipGrace,
  buildReferenceChipTooltipText,
  dismissReferenceChipTooltip,
  removeAllPortaledReferenceChipTooltips,
  shouldIgnoreChipTooltipHover,
  showReferenceChipTooltip,
} from './contenteditable-chat-input.chip-tooltip'

export {
  dismissReferenceChipTooltip,
  removeAllPortaledReferenceChipTooltips,
} from './contenteditable-chat-input.chip-tooltip'

interface UseContentEditableHandlersDeps {
  editorRef: React.RefObject<HTMLDivElement | null>
  isUpdatingRef: React.MutableRefObject<boolean>
  lastPasteTimeRef: React.MutableRefObject<number>
  pastedImagesRef: React.MutableRefObject<ChatImage[]>
  onImagesChangeRef: React.MutableRefObject<((images: ChatImage[]) => void) | undefined>
  /** Import local .md/.txt dropped on composer; return true if handled. */
  onLocalTextFilesDropRef?: React.MutableRefObject<
    ((files: File[]) => Promise<boolean>) | undefined
  >
  /** Import local audio dropped on composer; return true if handled. */
  onLocalAudioFilesDropRef?: React.MutableRefObject<
    ((files: File[]) => Promise<boolean>) | undefined
  >
  /** Long plain-text paste -> create doc + chip; return true if handled. */
  onLongTextPasteRef?: React.MutableRefObject<
    ((text: string) => Promise<boolean>) | undefined
  >
  mentionQueryRef: React.MutableRefObject<string>
  /** Saved `@mention` range while autocomplete is open (see handleInput). */
  mentionInsertRangeRef: React.MutableRefObject<Range | null>
  references: DocumentReference[]
  onChange: (text: string) => void
  onReferencesChange: (references: DocumentReference[]) => void
  onInsertReference?: (reference: DocumentReference) => void
  onRemoveReference?: (id: string, ref?: DocumentReference) => void
  onOpenReference?: (reference: DocumentReference, event: unknown) => void
  onActiveChatSkillChange?: (skill: { id: string; name: string } | null) => void
  setMentionQuery: (query: string) => void
  setMentionPosition: (position: { top: number; left: number } | null) => void
  setSlashQuery: (query: string) => void
  setSlashPosition: (position: { top: number; left: number } | null) => void
  setPastedImages: (images: ChatImage[]) => void
  disabled: boolean
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void
  mentionPosition: { top: number; left: number } | null
  slashPosition: { top: number; left: number } | null
  openTabIds?: string[]
}

export function useContentEditableHandlers(deps: UseContentEditableHandlersDeps) {
  const {
    editorRef,
    isUpdatingRef,
    lastPasteTimeRef,
    pastedImagesRef,
    onImagesChangeRef,
    onLocalTextFilesDropRef,
    onLocalAudioFilesDropRef,
    onLongTextPasteRef,
    mentionQueryRef,
    mentionInsertRangeRef,
    references,
    onChange,
    onReferencesChange,
    onInsertReference,
    onRemoveReference,
    onOpenReference,
    onActiveChatSkillChange,
    setMentionQuery,
    setMentionPosition,
    setSlashQuery,
    setSlashPosition,
    setPastedImages,
    disabled,
    onKeyDown,
    mentionPosition,
    slashPosition,
    openTabIds,
  } = deps

  // Parse file reference from text (e.g., "@App.tsx (22-23)" or "App.tsx (22-23)")
  // Only works for markdown documents (.md files)
  const parseFileReference = useCallback((text: string): { filePath: string; lineStart?: number; lineEnd?: number } | null => {
    // Match patterns like:
    // - @App.tsx (22-23)
    // - App.tsx (22-23)
    // - @frontend/src/App.tsx (22-23)
    // - App.tsx (22) - single line
    const match = text.match(/@?([^\s(]+)\s*\((\d+)(?:-(\d+))?\)/)
    if (match) {
      const filePath = match[1]
      // Only create chip if it's a markdown file (.md extension)
      if (filePath.endsWith('.md') || filePath.includes('.md')) {
        return {
          filePath: filePath,
          lineStart: parseInt(match[2], 10),
          lineEnd: match[3] ? parseInt(match[3], 10) : parseInt(match[2], 10)
        }
      }
    }
    return null
  }, [])

  // Read a File/Blob, downscaled, as a ChatImage
  const readImageFile = useCallback((file: File): Promise<ChatImage> => {
    return resizeChatImageFile(file)
  }, [])

  /** Append dropped image files to staged chat images. Returns true if any image was queued. */
  const appendDroppedImageFiles = useCallback(
    (files: File[]) => {
      const imageFiles = files.filter((f) => f.type.startsWith('image/'))
      if (imageFiles.length === 0) return false
      void Promise.all(imageFiles.map((f) => readImageFile(f))).then((imgs) => {
        const validation = validateStagedImages(pastedImagesRef.current, imgs)
        if (!validation.ok) {
          toast.error(CHAT_IMAGE_BUDGET_TOAST_TITLE, {
            description: validation.message,
            duration: 8000,
          })
          return
        }
        const updated = [...pastedImagesRef.current, ...imgs]
        pastedImagesRef.current = updated
        setPastedImages(updated)
        onImagesChangeRef.current?.(updated)
      })
      return true
    },
    [readImageFile, setPastedImages]
  )

  /** When chat pane captures a drop outside the editor (e.g. messages area). */
  const applyNativeFileDrop = useCallback(
    (files: FileList) => {
      editorRef.current?.focus()
      const { textFiles, audioFiles, rest } = partitionComposerDropFiles(Array.from(files))
      if (textFiles.length > 0 && onLocalTextFilesDropRef?.current) {
        void onLocalTextFilesDropRef.current(textFiles)
      }
      if (audioFiles.length > 0 && onLocalAudioFilesDropRef?.current) {
        void onLocalAudioFilesDropRef.current(audioFiles)
      }
      const handledMedia = appendDroppedImageFiles(rest)
      return textFiles.length > 0 || audioFiles.length > 0 || handledMedia
    },
    [appendDroppedImageFiles, onLocalTextFilesDropRef, onLocalAudioFilesDropRef]
  )

  // Insert reference chip inline at cursor
  const insertReferenceAtCursor = useCallback((reference: DocumentReference) => {
    if (!editorRef.current) return

    // Skip only when the same ref is already in BOTH parent state and the editor DOM.
    // Parent can list a doc (e.g. Add to chat) before a chip exists; then useReferenceSync
    // would hit this path with references already updated - we still need to insert into DOM.
    const alreadyListed = referenceListedWithSameKey(references, reference)
    const chipPresent = documentReferenceChipPresentInEditor(editorRef.current, reference)
    if (alreadyListed && chipPresent) {
      console.log('[ContentEditableChatInput] Duplicate reference skipped:', reference)
      return
    }

    removeAllPortaledReferenceChipTooltips()

    const selection = window.getSelection()
    let range: Range | null = null
    
    if (!selection || selection.rangeCount === 0) {
      // No cursor/selection - create range at end of content
      if (editorRef.current) {
        range = document.createRange()
        range.selectNodeContents(editorRef.current)
        range.collapse(false) // Collapse to end
      } else {
        // Fallback: just add to references array
        onReferencesChange([...references, reference])
        onInsertReference?.(reference)
        return
      }
    } else {
      const selRange = selection.getRangeAt(0)
      // Only use the selection if it's actually inside our editor.
      // If focus is elsewhere (e.g. a button in a panel that dispatched add-to-chat),
      // fall back to inserting at the end of the editor so the chip lands in the right place.
      if (editorRef.current?.contains(selRange.commonAncestorContainer)) {
        range = selRange
      } else {
        range = document.createRange()
        range.selectNodeContents(editorRef.current)
        range.collapse(false) // Collapse to end
      }
    }
    
    if (!range) {
      // Still no range - fallback
      onReferencesChange([...references, reference])
      onInsertReference?.(reference)
      return
    }

    isUpdatingRef.current = true
    
    // Determine if this is a pasted reference (has line numbers)
    const isPasted = reference.lineStart !== undefined || reference.lineEnd !== undefined
    
    // Create chip container - renderChipAsHTML already returns a span, so use it directly
    const chipHTML = renderChipAsHTML(reference, false, isPasted)
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = chipHTML
    const chipContainer = tempDiv.firstElementChild as HTMLElement
    if (!chipContainer) {
      isUpdatingRef.current = false
      return
    }
    
    // Ensure attributes are set
    chipContainer.setAttribute('data-reference-id', reference.id)
    if (reference.lineStart !== undefined) {
      chipContainer.setAttribute('data-line-start', String(reference.lineStart))
    }
    if (reference.lineEnd !== undefined) {
      chipContainer.setAttribute('data-line-end', String(reference.lineEnd))
    }
    chipContainer.setAttribute('data-reference-type', 'chip')
    chipContainer.setAttribute('contenteditable', 'false')
    
    // Don't set title attribute - we'll use custom tooltip instead
    // (title attribute creates browser default tooltip which conflicts with our custom one)
    
    // Add space before and after chip so cursor lands after it with a gap
    const spaceBefore = document.createTextNode(' ')
    const spaceAfter = document.createTextNode(' ')
    
    // Insert: spaceBefore + chip + spaceAfter
    range.deleteContents()
    range.insertNode(spaceBefore)
    range.setStartAfter(spaceBefore)
    range.insertNode(chipContainer)
    range.setStartAfter(chipContainer)
    range.insertNode(spaceAfter)
    range.setStartAfter(spaceAfter)
    range.collapse(true)
    
    // Update selection (create one if it doesn't exist)
    const currentSelection = window.getSelection()
    if (currentSelection) {
      currentSelection.removeAllRanges()
      currentSelection.addRange(range)
    }
    
    // Notify parent only when this ref was not already in props (avoid duplicate ids in callbacks)
    if (!alreadyListed) {
      onReferencesChange([...references, reference])
      onInsertReference?.(reference)
    }
    
    // Attach remove handler to chip - also syncs with "Current References" section
    const removeButton = chipContainer.querySelector('[data-reference-remove]')
    if (removeButton) {
      removeButton.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        // Remove from references array (match by ID + line range for multiple refs to same doc)
        // Pass full reference so parent can do exact match (ID + line range)
        onRemoveReference?.(reference.id, reference)
        onReferencesChange(references.filter(r => {
          if (r.id !== reference.id) return true
          // If same ID, must match line range exactly
          return r.lineStart !== reference.lineStart || r.lineEnd !== reference.lineEnd
        }))
        dismissReferenceChipTooltip(chipContainer)
        // Remove chip from DOM
        chipContainer.remove()
        // Sync text state
        const text = extractPlainText(editorRef.current!)
        onChange(text)
        editorRef.current?.focus()
      })
    }

    chipContainer.addEventListener('click', (e) => {
      const target = e.target as Element
      if (target.closest('[data-reference-remove]')) return
      e.preventDefault()
      e.stopPropagation()
      onOpenReference?.(reference, e)
    })
    
    // Add tooltip on hover (for HTML-rendered chips).
    // Paste inserts the chip under the cursor: skip that first synthetic hover,
    // and dismiss on pointer-away even if the chip node is replaced (no mouseleave).
    armReferenceChipTooltipGrace(chipContainer)
    chipContainer.addEventListener('mouseenter', () => {
      if (shouldIgnoreChipTooltipHover(chipContainer)) return
      showReferenceChipTooltip(
        chipContainer,
        buildReferenceChipTooltipText(reference),
      )
    })
    
    chipContainer.addEventListener('mouseleave', () => {
      dismissReferenceChipTooltip(chipContainer)
    })
    
    // Sync text state (extract plain text without chips)
    const text = extractPlainText(editorRef.current)
    onChange(text)

    requestAnimationFrame(() => {
      isUpdatingRef.current = false
    })
    
    // Restore focus and cursor position (chip may be removed by a sync effect if anchor is stale)
    setTimeout(() => {
      const editor = editorRef.current
      if (!editor) return
      editor.focus()
      const newRange = document.createRange()
      if (spaceAfter.isConnected) {
        newRange.setStartAfter(spaceAfter)
      } else if (chipContainer.isConnected) {
        newRange.setStartAfter(chipContainer)
      } else {
        newRange.selectNodeContents(editor)
        newRange.collapse(false)
      }
      newRange.collapse(true)
      const sel = window.getSelection()
      if (sel) {
        sel.removeAllRanges()
        sel.addRange(newRange)
      }
    }, 0)
  }, [references, onReferencesChange, onInsertReference, onRemoveReference, onChange, onOpenReference, editorRef, isUpdatingRef])

  // Handle input - extract plain text and detect @ mentions
  const handleInput = useCallback(() => {
    if (!editorRef.current || isUpdatingRef.current) return

    // Extract text, removing reference markers
    const text = extractPlainText(editorRef.current)
    onChange(text)

    // Check for @ mention trigger
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      setMentionQuery('')
      setMentionPosition(null)
      return
    }

    const range = selection.getRangeAt(0)
    const textNode = range.startContainer
    if (textNode.nodeType !== Node.TEXT_NODE) {
      setMentionQuery('')
      setMentionPosition(null)
      return
    }

    const textContent = textNode.textContent || ''
    const cursorOffset = range.startOffset

    // Find @ symbol before cursor
    const textBeforeCursor = textContent.slice(0, cursorOffset)
    const mentionMatch = parseMentionQueryAtCursor(textBeforeCursor)

    if (mentionMatch) {
      setSlashPosition(null)
      setSlashQuery('')
      const query = mentionMatch.query
      mentionQueryRef.current = query
      setMentionQuery(query)

      const mentionStart = cursorOffset - mentionMatch.full.length
      const anchorRange = document.createRange()
      anchorRange.setStart(textNode, mentionStart)
      anchorRange.setEnd(textNode, cursorOffset)
      mentionInsertRangeRef.current = anchorRange.cloneRange()

      // Calculate position for dropdown (above cursor, aligned to @ symbol)
      // Use getBoundingClientRect() for viewport-relative positioning (portal renders at body level)
      const rect = range.getBoundingClientRect()

      // Find the @ symbol position by creating a range at the start of the mention
      const atSymbolRange = document.createRange()
      atSymbolRange.setStart(textNode, mentionStart)
      atSymbolRange.setEnd(textNode, mentionStart + 1)
      const atSymbolRect = atSymbolRange.getBoundingClientRect()

      // Position dropdown just above the @ symbol line, growing upwards
      const dropdownWidth = 420 // Width from component
      const padding = 8 // Padding from @ symbol

      // Get the input container's position for relative positioning
      const inputContainer = editorRef.current?.closest('.rounded-lg')
      const containerRect = inputContainer?.getBoundingClientRect()

      if (!containerRect) {
        setMentionPosition(null)
        return
      }

      // Position dropdown just above the @ symbol line
      // The dropdown will use translateY(-100%) to grow upwards from this position
      const topPosition = atSymbolRect.top // Top of the line containing @

      // Use @ symbol's left position, relative to viewport
      const leftPosition = atSymbolRect.left

      // Adjust horizontal position if would go off right edge
      const viewportRight = window.innerWidth
      const wouldOverflowRight = leftPosition + dropdownWidth > viewportRight
      const adjustedLeft = wouldOverflowRight
        ? Math.max(10, viewportRight - dropdownWidth - 10) // Keep within viewport bounds
        : leftPosition

      // Ensure dropdown doesn't go above viewport (with some padding)
      const viewportTop = 10
      const finalTop = Math.max(viewportTop, topPosition)

      setMentionPosition({
        top: finalTop,
        left: adjustedLeft
      })
    } else {
      mentionInsertRangeRef.current = null
      setMentionQuery('')
      setMentionPosition(null)

      const slashMatch = textBeforeCursor.match(/(?:^|\s)\/([\w-]*)$/)
      if (slashMatch) {
        setSlashQuery(slashMatch[1] ?? '')
        const regionStart = cursorOffset - slashMatch[0].length
        const slashOffsetInMatch = slashMatch[0].indexOf('/')
        const slashCharOffset = regionStart + slashOffsetInMatch

        const slashSymbolRange = document.createRange()
        slashSymbolRange.setStart(textNode, slashCharOffset)
        slashSymbolRange.setEnd(textNode, Math.min(slashCharOffset + 1, textContent.length))
        const slashRect = slashSymbolRange.getBoundingClientRect()

        const inputContainer = editorRef.current?.closest('.rounded-lg')
        const containerRect = inputContainer?.getBoundingClientRect()
        if (!containerRect) {
          setSlashPosition(null)
          return
        }

        const dropdownWidth = 420
        const topPosition = slashRect.top
        const leftPosition = slashRect.left
        const viewportRight = window.innerWidth
        const wouldOverflowRight = leftPosition + dropdownWidth > viewportRight
        const adjustedLeft = wouldOverflowRight
          ? Math.max(10, viewportRight - dropdownWidth - 10)
          : leftPosition
        const finalSlashTop = Math.max(10, topPosition)

        setSlashPosition({
          top: finalSlashTop,
          left: adjustedLeft,
        })
      } else {
        setSlashQuery('')
        setSlashPosition(null)
      }
    }
  }, [onChange, editorRef, isUpdatingRef, mentionQueryRef, mentionInsertRangeRef, setMentionQuery, setMentionPosition, setSlashQuery, setSlashPosition])

  /** Delete the active `@mention` at the saved or live cursor range; returns range for chip insert. */
  const deleteMentionTriggerAtCursor = useCallback((): Range | null => {
    const root = editorRef.current
    if (!root) return null

    const saved = mentionInsertRangeRef.current
    mentionInsertRangeRef.current = null

    if (saved) {
      try {
        if (!root.contains(saved.startContainer)) return null
        saved.deleteContents()
        const insertRange = saved.cloneRange()
        insertRange.collapse(true)
        return insertRange
      } catch {
        // Saved range went stale; fall through to live selection.
      }
    }

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return null

    const range = selection.getRangeAt(0)
    if (!root.contains(range.commonAncestorContainer)) return null

    const tn = range.startContainer
    if (tn.nodeType !== Node.TEXT_NODE) return range.cloneRange()

    const tc = tn.textContent || ''
    const before = tc.slice(0, range.startOffset)
    const mentionMatch = parseMentionQueryAtCursor(before)
    if (!mentionMatch) return range.cloneRange()

    const delStart = range.startOffset - mentionMatch.full.length
    const textRange = document.createRange()
    textRange.setStart(tn, delStart)
    textRange.setEnd(tn, range.startOffset)
    textRange.deleteContents()
    const insertRange = textRange.cloneRange()
    insertRange.collapse(true)
    return insertRange
  }, [editorRef, mentionInsertRangeRef])

  // Handle mention selection - insert chip inline
  const handleMentionSelect = useCallback((reference: DocumentReference) => {
    if (!editorRef.current) return

    const alreadyListed = referenceListedWithSameKey(references, reference)
    const chipPresent = documentReferenceChipPresentInEditor(editorRef.current, reference)
    if (alreadyListed && chipPresent) {
      mentionInsertRangeRef.current = null
      setMentionQuery('')
      setMentionPosition(null)
      return
    }

    isUpdatingRef.current = true

    const insertRange = deleteMentionTriggerAtCursor()
    if (insertRange) {
      const selection = window.getSelection()
      if (selection) {
        selection.removeAllRanges()
        selection.addRange(insertRange)
      }
    }

    if (!documentReferenceChipPresentInEditor(editorRef.current, reference)) {
      insertReferenceAtCursor(reference)
    } else {
      if (!alreadyListed) {
        onReferencesChange([...references, reference])
        onInsertReference?.(reference)
      }
      const text = extractPlainText(editorRef.current)
      onChange(text)
      requestAnimationFrame(() => {
        isUpdatingRef.current = false
      })
    }

    setMentionQuery('')
    setMentionPosition(null)
  }, [references, onChange, onReferencesChange, onInsertReference, insertReferenceAtCursor, deleteMentionTriggerAtCursor, editorRef, isUpdatingRef, mentionInsertRangeRef, setMentionQuery, setMentionPosition])

  const handleSlashSelect = useCallback((skill: SlashSkillRow) => {
    if (!editorRef.current) return
    onActiveChatSkillChange?.({ id: skill.id, name: skill.name })

    // Remove any existing skill chip (only one skill chip at a time)
    editorRef.current.querySelectorAll('[data-skill-id]').forEach(el => el.remove())

    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      const tn = range.startContainer
      if (tn.nodeType === Node.TEXT_NODE) {
        const tc = tn.textContent || ''
        const cursorOffset = range.startOffset
        const before = tc.slice(0, cursorOffset)
        const slashMatch = before.match(/(?:^|\s)\/([\w-]*)$/)
        if (slashMatch) {
          const delStart = cursorOffset - slashMatch[0].length
          const textRange = document.createRange()
          textRange.setStart(tn, delStart)
          textRange.setEnd(tn, cursorOffset)
          textRange.deleteContents()
        }
      }

      // Insert skill chip at current cursor position
      if (selection.rangeCount > 0) {
        const insertRange = selection.getRangeAt(0)
        const chipHTML = renderSkillChipAsHTML({ id: skill.id, name: skill.name })
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = chipHTML
        const chipEl = tempDiv.firstElementChild as HTMLElement
        if (chipEl) {
          const spaceBefore = document.createTextNode('\u00A0')
          const spaceAfter = document.createTextNode('\u00A0')
          insertRange.insertNode(spaceBefore)
          insertRange.setStartAfter(spaceBefore)
          insertRange.insertNode(chipEl)
          insertRange.setStartAfter(chipEl)
          insertRange.insertNode(spaceAfter)
          insertRange.setStartAfter(spaceAfter)
          insertRange.collapse(true)
          selection.removeAllRanges()
          selection.addRange(insertRange)

          // Attach remove handler
          const removeBtn = chipEl.querySelector('[data-skill-remove]')
          if (removeBtn) {
            removeBtn.addEventListener('click', (e) => {
              e.preventDefault()
              e.stopPropagation()
              chipEl.remove()
              onActiveChatSkillChange?.(null)
              const text = extractPlainText(editorRef.current!)
              onChange(text)
              editorRef.current?.focus()
            })
          }
        }
      }
    }

    setSlashPosition(null)
    setSlashQuery('')
    const text = extractPlainText(editorRef.current)
    onChange(text)
    setTimeout(() => editorRef.current?.focus(), 0)
  }, [onChange, onActiveChatSkillChange, editorRef, setSlashPosition, setSlashQuery])

  // Handle keydown
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return

    // Escape: close any open menu first; only forward to parent if no menu was open
    if (e.key === 'Escape') {
      if (mentionPosition !== null || slashPosition !== null) {
        e.preventDefault()
        mentionInsertRangeRef.current = null
        setMentionQuery('')
        setMentionPosition(null)
        setSlashQuery('')
        setSlashPosition(null)
        return
      }
      onKeyDown?.(e)
      return
    }

    // Backspace: if a menu is open and the cursor is right after the trigger char,
    // closing is handled by handleInput (which re-scans after the char is removed).
    // No special logic needed here — fall through to chip handling below.

    // If mention menu is open, let it handle navigation (check position, not query — query can be '' after just '@')
    if (mentionPosition !== null) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
        // MentionAutocomplete will handle these via its own window keydown listener
        return
      }
    }

    if (slashPosition !== null) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
        return
      }
    }

    // Handle backspace/delete on chips
    // Chips are contenteditable=false so the browser treats them as a single unit,
    // but it won't fire our handler reliably. We intercept here and handle manually.
    if (e.key === 'Backspace' || e.key === 'Delete') {
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        onKeyDown?.(e)
        return
      }

      const range = selection.getRangeAt(0)
      let chipElement: Element | null = null

      if (!range.collapsed) {
        // Selection spanning multiple nodes: find any chip within the selection
        const fragment = range.cloneContents()
        const selected = fragment.querySelector('[data-reference-id]')
        if (selected) {
          // Find the actual DOM element (not the cloned one)
          const chipId = selected.getAttribute('data-reference-id')
          if (chipId) {
            chipElement = editorRef.current!.querySelector(`[data-reference-id="${CSS.escape(chipId)}"]`)
          }
        }
      } else if (e.key === 'Backspace') {
        // Walk backwards from the cursor to find an adjacent chip.
        // We look through the parent's child nodes for a chip immediately
        // before the current cursor position (accounting for empty text nodes).
        const { startContainer, startOffset } = range

        // Case 1: cursor is at offset 0 in a text node — look at previous siblings of parent
        // Case 2: cursor is inside the editor element itself (not a text node) — look at child before offset
        let nodeToCheckBefore: Node | null = null

        if (startContainer.nodeType === Node.TEXT_NODE) {
          if (startOffset === 0) {
            // At the very start of this text node — check the node before it
            nodeToCheckBefore = startContainer.previousSibling
          }
          // If startOffset > 0 there's real text before cursor; let browser handle
        } else {
          // Cursor is in the editor div itself; check the child at (startOffset - 1)
          if (startOffset > 0) {
            nodeToCheckBefore = startContainer.childNodes[startOffset - 1]
          }
        }

        // Skip over empty text nodes (browser sometimes inserts zero-width nodes)
        while (
          nodeToCheckBefore &&
          nodeToCheckBefore.nodeType === Node.TEXT_NODE &&
          (nodeToCheckBefore.textContent || '').trim() === ''
        ) {
          nodeToCheckBefore = nodeToCheckBefore.previousSibling
        }

        if (nodeToCheckBefore && nodeToCheckBefore.nodeType === Node.ELEMENT_NODE) {
          const el = nodeToCheckBefore as Element
          chipElement = el.hasAttribute('data-reference-id') ? el : (el.closest('[data-reference-id]') ?? (el.hasAttribute('data-skill-id') ? el : el.closest('[data-skill-id]')))
        }
      } else if (e.key === 'Delete') {
        const { startContainer, startOffset } = range
        let nodeToCheckAfter: Node | null = null

        if (startContainer.nodeType === Node.TEXT_NODE) {
          const textNode = startContainer as Text
          if (startOffset === textNode.length) {
            nodeToCheckAfter = textNode.nextSibling
          }
        } else {
          nodeToCheckAfter = startContainer.childNodes[startOffset] ?? null
        }

        while (
          nodeToCheckAfter &&
          nodeToCheckAfter.nodeType === Node.TEXT_NODE &&
          (nodeToCheckAfter.textContent || '').trim() === ''
        ) {
          nodeToCheckAfter = nodeToCheckAfter.nextSibling
        }

        if (nodeToCheckAfter && nodeToCheckAfter.nodeType === Node.ELEMENT_NODE) {
          const el = nodeToCheckAfter as Element
          chipElement = el.hasAttribute('data-reference-id') ? el : (el.closest('[data-reference-id]') ?? (el.hasAttribute('data-skill-id') ? el : el.closest('[data-skill-id]')))
        }
      }

      if (chipElement) {
        const chipId = chipElement.getAttribute('data-reference-id')
        const skillId = chipElement.getAttribute('data-skill-id')

        if (skillId) {
          // Skill chip — remove it and clear active skill
          e.preventDefault()
          const newRange = document.createRange()
          newRange.setStartBefore(chipElement)
          newRange.collapse(true)
          selection.removeAllRanges()
          selection.addRange(newRange)
          chipElement.remove()
          onActiveChatSkillChange?.(null)
          const text = extractPlainText(editorRef.current!)
          onChange(text)
          editorRef.current?.focus()
          return
        }

        if (chipId) {
          e.preventDefault()

          // Find the reference to remove — match by text content for line ranges
          const chipText = chipElement.textContent || ''
          const lineMatch = chipText.match(/\((\d+)(?:-(\d+))?\)/)
          const chipLineStart = lineMatch ? parseInt(lineMatch[1], 10) : undefined
          const chipLineEnd = lineMatch && lineMatch[2] ? parseInt(lineMatch[2], 10) : chipLineStart

          const refToRemove = references.find(r => {
            if (r.id !== chipId) return false
            if (chipLineStart !== undefined) {
              return r.lineStart === chipLineStart && r.lineEnd === chipLineEnd
            }
            return r.lineStart === undefined
          }) ?? references.find(r => r.id === chipId)

          if (refToRemove) {
            onRemoveReference?.(refToRemove.id, refToRemove)
            onReferencesChange(references.filter(r => {
              if (r.id !== refToRemove.id) return true
              return r.lineStart !== refToRemove.lineStart || r.lineEnd !== refToRemove.lineEnd
            }))
          } else {
            onRemoveReference?.(chipId)
            onReferencesChange(references.filter(r => r.id !== chipId))
          }

          // Place cursor just before the chip before removing it (so caret stays in the right spot)
          const newRange = document.createRange()
          newRange.setStartBefore(chipElement)
          newRange.collapse(true)
          selection.removeAllRanges()
          selection.addRange(newRange)

          dismissReferenceChipTooltip(chipElement as HTMLElement)
          chipElement.remove()
          const text = extractPlainText(editorRef.current!)
          onChange(text)
          editorRef.current?.focus()
          return
        }
      }
    }

    // Handle Enter (submit) - but not if mention or slash menu is open
    if (e.key === 'Enter' && !e.shiftKey && !mentionPosition && !slashPosition) {
      e.preventDefault()
      onKeyDown?.(e)
      return
    }

    onKeyDown?.(e)
  }, [disabled, onKeyDown, onRemoveReference, onReferencesChange, references, mentionPosition, slashPosition, mentionInsertRangeRef, editorRef, onActiveChatSkillChange, onChange])

  // Handle paste - detect file references from markdown documents only
  const handlePaste = useCallback((e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()

    // Prevent duplicate paste events (browser sometimes fires multiple times)
    const now = Date.now()
    if (now - lastPasteTimeRef.current < 100) {
      return
    }
    lastPasteTimeRef.current = now

    // Check for image items in clipboard FIRST (before text handling)
    const items = Array.from(e.clipboardData.items)
    const imageItem = items.find(item => item.type.startsWith('image/'))
    if (imageItem) {
      const file = imageItem.getAsFile()
      if (file) {
        readImageFile(file).then(img => {
          const validation = validateStagedImages(pastedImagesRef.current, [img])
          if (!validation.ok) {
            toast.error(CHAT_IMAGE_BUDGET_TOAST_TITLE, {
              description: validation.message,
              duration: 8000,
            })
            return
          }
          const updated = [...pastedImagesRef.current, img]
          pastedImagesRef.current = updated
          setPastedImages(updated)
          onImagesChangeRef.current?.(updated)
        })
        return
      }
    }

    // Check for custom clipboard data (from our markdown editor)
    // Try both application/json and text/html (some browsers use different formats)
    let customData = e.clipboardData.getData('application/json')
    if (!customData) {
      // Fallback: check text/html for embedded JSON
      const htmlData = e.clipboardData.getData('text/html')
      if (htmlData) {
        const jsonMatch = htmlData.match(/data-flownotes-reference="([^"]+)"/)
        if (jsonMatch) {
          try {
            customData = decodeURIComponent(jsonMatch[1])
          } catch {}
        }
      }
    }
    
    if (customData) {
      try {
        const data = JSON.parse(customData)
        // Only process if it's a markdown document reference
        if (data.type === 'document-reference' && data.documentId) {
          // Verify it's a markdown file (or allow all document types)
          const isMarkdown = data.filePath?.endsWith('.md') ||
                           data.filePath?.includes('.md') ||
                           data.documentType === 'note' ||
                           data.documentType === 'work_spec' ||
                           !data.filePath // Allow if no filePath specified

          if (isMarkdown) {
            const reference: DocumentReference = {
              id: data.documentId,
              title: data.title || data.filePath?.split('/').pop() || 'Document',
              type: data.documentType || 'note',
              path: data.filePath,
              lineStart: data.lineStart,
              lineEnd: data.lineEnd,
              excerpt: clipPasteReferenceExcerpt(typeof data.text === 'string' ? data.text : ''),
              excerptTail: clipPasteReferenceTail(typeof data.text === 'string' ? data.text : '') || undefined,
            }
            
            // Insert chip inline - don't insert text, just the chip
            insertReferenceAtCursor(reference)
            
            // Don't insert the text - the chip is the reference
            return
          }
        }
      } catch (err) {
        // Not JSON data, continue with text handling
      }
    }

    const text = e.clipboardData.getData('text/plain')

    // Check if pasted text contains a file reference pattern (only for .md files)
    const fileRef = parseFileReference(text)
    if (fileRef) {
      // Create a reference from the file path
      // Try to find matching markdown document in open tabs
      const matchingTabId = openTabIds?.find(tabId => {
        // Only match markdown files
        if (!tabId.endsWith('.md') && !tabId.includes('.md')) return false
        // Simple matching - could be enhanced
        return tabId.includes(fileRef.filePath.split('/').pop() || '') ||
               fileRef.filePath.includes(tabId)
      })

      if (matchingTabId) {
        const reference: DocumentReference = {
          id: matchingTabId,
          title: fileRef.filePath.split('/').pop() || fileRef.filePath,
          type: 'note',
          path: fileRef.filePath,
          lineStart: fileRef.lineStart,
          lineEnd: fileRef.lineEnd
        }
        insertReferenceAtCursor(reference)

        // Remove the file reference pattern from text and insert the rest
        const textWithoutRef = text.replace(/@?[^\s(]+.*\(\d+(?:-\d+)?\)/, '').trim()
        if (textWithoutRef) {
          const selection = window.getSelection()
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0)
            const textNode = document.createTextNode(' ' + textWithoutRef + ' ')
            range.insertNode(textNode)
            range.setStartAfter(textNode)
            range.collapse(true)
            selection.removeAllRanges()
            selection.addRange(range)
            handleInput()
          }
        }
        return
      }
    }

    // Check if pasted text is a standalone URL - convert to a chip
    const trimmedText = text.trim()
    if (/^https?:\/\/[^\s]+$/.test(trimmedText)) {
      const chipHtml = renderUrlChipAsHTML(trimmedText)
      const urlSelection = window.getSelection()
      if (urlSelection && urlSelection.rangeCount > 0) {
        const urlRange = urlSelection.getRangeAt(0)
        urlRange.deleteContents()
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = chipHtml
        const chipEl = tempDiv.firstElementChild as HTMLElement
        if (chipEl) {
          // Add click handler to open URL in new tab
          chipEl.addEventListener('click', () => window.open(trimmedText, '_blank', 'noopener,noreferrer'))
          // Insert a space after the chip so cursor lands outside it
          const spaceNode = document.createTextNode('\u00A0')
          urlRange.insertNode(spaceNode)
          urlRange.insertNode(chipEl)
          urlRange.setStartAfter(spaceNode)
          urlRange.collapse(true)
          urlSelection.removeAllRanges()
          urlSelection.addRange(urlRange)
          handleInput()
        }
      }
      return
    }

    // Check if pasted text is a code block (``` fenced) - convert to a styled code block chip
    const codeBlockMatch = trimmedText.match(/^```(\w*)\r?\n([\s\S]*?)\r?\n?```$/)
    if (codeBlockMatch) {
      const lang = codeBlockMatch[1] || ''
      const code = codeBlockMatch[2]
      const blockHtml = renderCodeBlockAsHTML(lang, code)
      const cbSelection = window.getSelection()
      if (cbSelection && cbSelection.rangeCount > 0) {
        const cbRange = cbSelection.getRangeAt(0)
        cbRange.deleteContents()
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = blockHtml
        const blockEl = tempDiv.firstElementChild as HTMLElement
        if (blockEl) {
          // Wire up remove button
          const removeBtn = blockEl.querySelector('[data-code-block-remove]')
          removeBtn?.addEventListener('click', () => {
            blockEl.remove()
            handleInput()
          })
          const spaceNode = document.createTextNode('\u00A0')
          cbRange.insertNode(spaceNode)
          cbRange.insertNode(blockEl)
          cbRange.setStartAfter(spaceNode)
          cbRange.collapse(true)
          cbSelection.removeAllRanges()
          cbSelection.addRange(cbRange)
          handleInput()
        }
      }
      return
    }

    // Long paste → create FlowNotes doc + chip (avoid stuffing transcripts into the prompt)
    if (text.trim() && isLongTextPaste(text) && onLongTextPasteRef?.current) {
      void onLongTextPasteRef.current(text).then((handled) => {
        if (handled) return
        const sel = window.getSelection()
        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0)
          range.deleteContents()
          const textNode = document.createTextNode(text)
          range.insertNode(textNode)
          range.setStartAfter(textNode)
          range.collapse(true)
          sel.removeAllRanges()
          sel.addRange(range)
          handleInput()
        }
      })
      return
    }

    // Regular paste - insert text at cursor
    // Basecamp todos/projects are NOT pasteable (only draggable)
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      range.deleteContents()
      const textNode = document.createTextNode(text)
      range.insertNode(textNode)
      range.setStartAfter(textNode)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)

      // Trigger input event
      handleInput()
    }
  }, [handleInput, parseFileReference, insertReferenceAtCursor, openTabIds, readImageFile, lastPasteTimeRef, pastedImagesRef, setPastedImages, onImagesChangeRef, onLongTextPasteRef])

  // Handle native drop events (for drag & drop from tabs/tree)
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    // Place cursor at the drop point so chips insert at the correct position
    // caretRangeFromPoint is WebKit/Blink; caretPositionFromPoint is the standard (Firefox)
    const setCursorAtDropPoint = () => {
      try {
        if ('caretRangeFromPoint' in document) {
          const range = (document as any).caretRangeFromPoint(e.clientX, e.clientY)
          if (range) {
            const sel = window.getSelection()
            if (sel) { sel.removeAllRanges(); sel.addRange(range) }
          }
        } else if ('caretPositionFromPoint' in document) {
          const pos = (document as any).caretPositionFromPoint(e.clientX, e.clientY)
          if (pos) {
            const range = (document as Document).createRange()
            range.setStart(pos.offsetNode, pos.offset)
            range.collapse(true)
            const sel = window.getSelection()
            if (sel) { sel.removeAllRanges(); sel.addRange(range) }
          }
        }
      } catch (_) {
        // Ignore errors - cursor will fall back to last known position
      }
    }

    setCursorAtDropPoint()

    const droppedFiles = Array.from(e.dataTransfer.files)
    const { textFiles, audioFiles, rest } = partitionComposerDropFiles(droppedFiles)
    if (textFiles.length > 0 && onLocalTextFilesDropRef?.current) {
      void onLocalTextFilesDropRef.current(textFiles)
    }
    if (audioFiles.length > 0 && onLocalAudioFilesDropRef?.current) {
      void onLocalAudioFilesDropRef.current(audioFiles)
    }
    if (appendDroppedImageFiles(rest)) {
      return
    }
    if (textFiles.length > 0 || audioFiles.length > 0) {
      return
    }

    // Try to get drag data (from sidebar tabs, document tree, Basecamp items, etc.)
    const dragData = e.dataTransfer.getData('application/json')
    if (dragData) {
      try {
        const data = JSON.parse(dragData)
        // Accept any drag data that has an id (documents, tabs, basecamp todos/projects, etc.)
        if (data.id) {
          const reference: DocumentReference = {
            id: data.id,
            title: data.title || data.name || 'Document',
            type: data.documentType || 'note',
            path: data.path
          }
          insertReferenceAtCursor(reference)
          return
        }
      } catch (err) {
        // Not JSON data, continue with other handling
      }
    }

    // Fallback: handle text drops
    const text = e.dataTransfer.getData('text/plain')
    if (text) {
      const selection = window.getSelection()
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0)
        range.deleteContents()
        const textNode = document.createTextNode(text)
        range.insertNode(textNode)
        range.setStartAfter(textNode)
        range.collapse(true)
        selection.removeAllRanges()
        selection.addRange(range)
        handleInput()
      }
    }
  }, [insertReferenceAtCursor, handleInput, appendDroppedImageFiles, onLocalTextFilesDropRef, onLocalAudioFilesDropRef])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    // Allow drop
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy'
    }
  }, [])

  return {
    handleInput,
    handleMentionSelect,
    handleSlashSelect,
    handleKeyDown,
    parseFileReference,
    readImageFile,
    handlePaste,
    handleDrop,
    handleDragOver,
    insertReferenceAtCursor,
    applyNativeFileDrop,
  }
}
